import React from 'react';
import { Loader2 } from 'lucide-react';
import type { CompatProgress, CompatReportDocument } from '../types';
import { giveOrTakePipes } from '../helpers/formatReport';
import { cn } from '../../../../../lib/utils';

interface ProgressViewProps {
  progress: CompatProgress | null;
  /** Frozen lot-phase progress — Steps 1–4 keep these when later phases overwrite live progress. */
  lotProgress?: CompatProgress | null;
  /** Frozen withoutThem snapshot — Step 5 keeps statement when shortlist overwrites live progress. */
  withoutThemProgress?: CompatProgress | null;
  estimatedRuns: number | null;
  phase: string;
  /** Used after complete if last poll cleared progress — fill numbers from result. */
  result?: CompatReportDocument | null;
  /** True when submit asked for pipesNeeded / pipesToDeliver (shortlist can follow lot). */
  shortlistRequested?: boolean;
  /** True once this job hit progress.phase "withoutThem" (or result has deadEnds). */
  withoutThemSeen?: boolean;
  /** Last submit body — orders / minOrders size the live step-3 bar. */
  lastRequest?: Record<string, unknown> | null;
}

type LiveStep = 1 | 2 | 3 | 4 | 5 | 6;
type RunPhase = 'lot' | 'withoutThem' | 'shortlist' | 'other';
/** waiting = not started; now = active; passed = behind but lot still running; done = finished (green 100%). */
type StepStatus = 'waiting' | 'now' | 'passed' | 'done';

/** Sample-ui bar colours. */
const COLOR = {
  red: '#EB1C37',
  amber: '#E6A100',
  green: '#1F9A3A',
  blue: '#00273F',
} as const;

function progressRunPhase(progress: CompatProgress | null | undefined): RunPhase {
  const p = progress?.phase;
  if (p === 'shortlist') return 'shortlist';
  if (p === 'withoutThem') return 'withoutThem';
  if (p == null || p === 'lot') return 'lot';
  return 'other';
}

function resolveCurrentStep(input: {
  jobPhase: string;
  progress: CompatProgress | null;
  lotSettled: boolean;
  /** True while lot-phase runs are still climbing toward the target. */
  lotRunsStillGoing: boolean;
  finalizing: boolean;
  expectShortlist: boolean;
  expectWithoutThem: boolean;
  hasShortlistResult: boolean;
  hasDeadEndsResult: boolean;
}): LiveStep {
  const {
    jobPhase,
    progress,
    lotSettled,
    lotRunsStillGoing,
    finalizing,
    expectShortlist,
    expectWithoutThem,
    hasShortlistResult,
    hasDeadEndsResult,
  } = input;
  const runPhase = progressRunPhase(progress);
  const shortlistStep: LiveStep = expectWithoutThem || hasDeadEndsResult ? 6 : 5;

  if (jobPhase === 'done') {
    if (hasShortlistResult || expectShortlist) return shortlistStep;
    if (hasDeadEndsResult || expectWithoutThem) return 5;
    return 4;
  }
  if (runPhase === 'shortlist') return shortlistStep;
  if (runPhase === 'withoutThem' || runPhase === 'other') return 5;

  // Step 1 — submit / first ticks before any lot runs land (must not skip to 2/3 → 100%).
  if (jobPhase === 'submitting' || !progress) return 1;
  const lotRuns = progress.runs ?? 0;
  if (runPhase === 'lot' && lotRuns === 0 && !lotSettled) return 1;

  // Count may settle early; while runs still climb, stay on step 2/3 (not Done / not Finalizing).
  if (lotRunsStillGoing) {
    if (lotRuns > 0 && lotRuns < 30) return 2;
    return 3;
  }

  if (finalizing) return 4;
  if (lotSettled && (expectShortlist || expectWithoutThem)) return 4;
  if (!lotSettled && lotRuns > 0 && lotRuns < 30) return 2;
  if (!lotSettled) return 3;
  return 4;
}

function ProgressBar({
  pct,
  color,
  status,
}: {
  pct: number;
  color: string;
  status: StepStatus;
}) {
  const clamped = Math.max(0, Math.min(100, Math.round(pct)));
  // Green full bar only when truly done. Passed/now keep sample colours.
  const fillColor =
    status === 'done' ? COLOR.green : status === 'waiting' ? '#E2E5EA' : color;
  const width =
    status === 'waiting' ? 0 : status === 'done' ? 100 : clamped;

  return (
    <div className="mt-2.5">
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <span className="text-[12px] font-semibold text-blueprint-blue tabular-nums">
          {status === 'waiting'
            ? 'Waiting'
            : status === 'done'
              ? '100% complete'
              : `${clamped}% complete`}
        </span>
        {status === 'now' ? (
          <span className="inline-flex items-center gap-1.5 text-[12px] text-hero-red font-medium">
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
            Running…
          </span>
        ) : status === 'done' ? (
          <span className="text-[12px] text-galvanizing-green font-medium">Done</span>
        ) : status === 'passed' ? (
          <span className="text-[12px] text-heritage-grey font-medium">Continuing…</span>
        ) : (
          <span className="text-[12px] text-heritage-grey font-medium">Up next</span>
        )}
      </div>
      <div className="relative h-3.5 rounded-full bg-page-bg">
        <div
          className="relative h-full rounded-full transition-all duration-500"
          style={{
            width: `${width}%`,
            background: fillColor,
          }}
        >
          {status === 'now' && width > 0 && (
            <span
              className="absolute right-0 top-1/2 translate-x-1/2 -translate-y-1/2 flex items-center justify-center z-10"
              aria-hidden
            >
              <span
                className="h-5 w-5 rounded-full bg-white border-2 shadow-sm flex items-center justify-center"
                style={{ borderColor: color }}
              >
                <Loader2 className="h-3 w-3 animate-spin" style={{ color }} />
              </span>
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({
  value,
  label,
  sub,
}: {
  value: React.ReactNode;
  label: string;
  sub: string;
}) {
  return (
    <div className="bg-page-bg border border-[#E2E5EA] rounded-[10px] p-3 text-center">
      <div className="font-heading font-bold text-[22px] text-blueprint-blue">{value}</div>
      <div className="text-[10px] font-semibold tracking-wide uppercase text-heritage-grey mt-1">
        {label}
      </div>
      <div className="text-[10px] text-heritage-grey mt-1">{sub}</div>
    </div>
  );
}

function StepCard({
  step,
  title,
  status,
  children,
}: {
  step: LiveStep;
  title: string;
  status: StepStatus;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        'rounded-xl border bg-white p-5 relative overflow-hidden',
        status === 'waiting' ? 'border-[#E2E5EA] opacity-80' : 'border-[#E2E5EA]'
      )}
    >
      <div
        className={cn(
          'absolute top-0 left-0 right-0 h-[3px]',
          status === 'done'
            ? 'bg-galvanizing-green'
            : status === 'now' || status === 'passed'
              ? 'bg-hero-red'
              : 'bg-[#E2E5EA]'
        )}
      />
      <p className="text-[11px] font-semibold tracking-wider uppercase text-heritage-grey mb-2">
        <span className={status === 'waiting' ? 'text-heritage-grey' : 'text-hero-red'}>
          Step {step}
        </span>
        {' · '}
        {title}
        {status === 'done' && (
          <span className="ml-2 normal-case tracking-normal text-galvanizing-green">· done</span>
        )}
        {status === 'now' && (
          <span className="ml-2 normal-case tracking-normal text-hero-red">· now</span>
        )}
        {status === 'passed' && (
          <span className="ml-2 normal-case tracking-normal text-hero-red">· running</span>
        )}
        {status === 'waiting' && (
          <span className="ml-2 normal-case tracking-normal text-heritage-grey">· waiting</span>
        )}
      </p>
      {children}
    </div>
  );
}

/**
 * Progress tab — sample-ui storyboard: all steps appear together with status.
 * Done steps show full (100%) green bars; the active step uses sample colours.
 */
export const ProgressView: React.FC<ProgressViewProps> = ({
  progress,
  lotProgress = null,
  withoutThemProgress = null,
  estimatedRuns,
  phase,
  result = null,
  shortlistRequested = false,
  withoutThemSeen = false,
  lastRequest = null,
}) => {
  const reportComplete = phase === 'done';
  const runPhase = progressRunPhase(progress);
  const inWithoutThemPhase = runPhase === 'withoutThem';
  const inShortlistPhase = runPhase === 'shortlist';
  const inOtherPhase = runPhase === 'other';
  const hasShortlistResult = Boolean(result?.shortlist);
  const hasDeadEndsResult = Boolean(result?.weldability?.deadEnds);
  const expectWithoutThem = withoutThemSeen || hasDeadEndsResult || inWithoutThemPhase;
  const expectShortlist = shortlistRequested || hasShortlistResult || inShortlistPhase;
  const shortlistStep: LiveStep = expectWithoutThem ? 6 : 5;

  const lotSource: CompatProgress | null =
    lotProgress ||
    (progress && progressRunPhase(progress) === 'lot' ? progress : null);

  const lotRuns =
    lotSource?.runs ??
    (reportComplete ? result?.run?.orders ?? 0 : 0);
  const lotWeldable =
    lotSource != null
      ? Math.round(lotSource.weldable)
      : result?.weldability
        ? Math.round(result.weldability.pipesPlacedAvg)
        : null;
  const lotPipesTotal =
    lotSource?.pipesTotal ?? result?.weldability?.pipesTotal ?? null;
  const lotGiveOrTake =
    lotSource != null
      ? giveOrTakePipes(0, lotSource.pipesTotal, lotSource.giveOrTake)
      : result?.weldability
        ? giveOrTakePipes(result.weldability.marginOfError, result.weldability.pipesTotal)
        : null;

  // Watch-list settled can flip early while runs keep climbing toward the target.
  const watchListSettled =
    lotSource?.settled === true ||
    (progressRunPhase(progress) === 'lot' && progress?.settled === true);

  const est = estimatedRuns ?? 385;
  const hint = estimatedRuns && estimatedRuns > 0 ? estimatedRuns : 100;
  const reqOrders =
    typeof lastRequest?.orders === 'number'
      ? lastRequest.orders
      : typeof lastRequest?.minOrders === 'number'
        ? lastRequest.minOrders
        : null;
  const runTarget = Math.max(
    30,
    result?.run?.maxOrders ?? 0,
    result?.run?.orders ?? 0,
    reqOrders ?? 0,
    hint * 7
  );

  // While lot runs are still updating, steps 2/3 must stay running — not green 100% Done.
  const lotRunsStillGoing =
    (phase === 'working' || phase === 'submitting') &&
    runPhase === 'lot' &&
    lotRuns < runTarget;

  const lotPhaseFinished =
    (!lotRunsStillGoing && watchListSettled) ||
    inWithoutThemPhase ||
    inShortlistPhase ||
    reportComplete;

  const finalizing =
    lotPhaseFinished &&
    phase === 'working' &&
    !inWithoutThemPhase &&
    !inShortlistPhase &&
    !inOtherPhase &&
    !expectShortlist &&
    !expectWithoutThem;

  const current = resolveCurrentStep({
    jobPhase: phase,
    progress,
    lotSettled: lotPhaseFinished,
    lotRunsStillGoing,
    finalizing:
      finalizing ||
      (lotPhaseFinished &&
        phase === 'working' &&
        !inWithoutThemPhase &&
        !inShortlistPhase &&
        !inOtherPhase &&
        (expectShortlist || expectWithoutThem)),
    expectShortlist,
    expectWithoutThem,
    hasShortlistResult,
    hasDeadEndsResult,
  });

  const lotSoftPct = Math.min(
    95,
    Math.max(8, Math.round((lotRuns / runTarget) * 100) || 8)
  );
  const shortlistRuns = inShortlistPhase ? progress?.runs ?? 0 : 0;
  const shortlistSoftPct = Math.min(
    95,
    Math.round((shortlistRuns / Math.max(runTarget, hint * 7)) * 100) ||
      (shortlistRuns > 0 ? 8 : 3)
  );

  const withoutThemRuns = inWithoutThemPhase
    ? progress?.runs ?? 0
    : withoutThemProgress?.runs ?? 0;
  const withoutThemSoftPct =
    withoutThemRuns > 0
      ? Math.min(95, Math.max(8, Math.round((withoutThemRuns / runTarget) * 100)))
      : 38;

  const weldLabel =
    lotWeldable != null && lotPipesTotal != null
      ? `${lotWeldable} of ${lotPipesTotal}`
      : '— of —';

  /**
   * Steps 2–3: green 100% Done only after lot runs finish (or report done).
   * Step 1 completes as soon as we move past submit / first ticks — do not hold it
   * at a low % until the whole lot finishes.
   */
  const statusOf = (step: LiveStep): StepStatus => {
    if (reportComplete) return 'done';

    if (lotRunsStillGoing && step >= 2 && step <= 3) {
      if (current === step) return 'now';
      if (current > step) return 'passed';
      return 'waiting';
    }

    if (current > step) return 'done';
    if (current === step) return 'now';
    return 'waiting';
  };

  /** Sample-ui fills while active/passed; full green only when done. */
  const pctFor = (step: LiveStep, activePct: number): number => {
    const s = statusOf(step);
    if (s === 'done') return 100;
    if (s === 'waiting') return 0;
    return activePct;
  };

  const deadEnds = result?.weldability?.deadEnds ?? null;
  const lotPipesForDeadEnds =
    lotPipesTotal ??
    withoutThemProgress?.pipesTotal ??
    (progress?.phase === 'withoutThem' ? progress.pipesTotal : null) ??
    result?.weldability?.pipesTotal ??
    result?.project?.pipeCount ??
    null;
  const withoutThemOrders =
    result?.run?.orders ??
    withoutThemProgress?.runs ??
    (progress?.phase === 'withoutThem' ? progress.runs : null) ??
    reqOrders ??
    null;

  const withoutThemStatementFromProgress =
    (progress?.phase === 'withoutThem' && progress.statement) ||
    withoutThemProgress?.statement ||
    (inOtherPhase && progress?.statement) ||
    null;

  const withoutThemStatementFromResult = deadEnds
    ? `Found ${deadEnds.pipes.length} pipe${deadEnds.pipes.length === 1 ? '' : 's'} that end${
        deadEnds.pipes.length === 1 ? 's' : ''
      } the line (${deadEnds.pipes.map((p) => p.pipeId).join(', ')})${
        lotPipesForDeadEnds != null ? ` of a ${lotPipesForDeadEnds.toLocaleString()}-pipe lot` : ''
      }. Rerunning ${(withoutThemOrders ?? 0).toLocaleString()} builds without ${
        deadEnds.pipes.length === 1 ? 'it' : 'them'
      } to measure the usable pool.`
    : null;

  const withoutThemStatement =
    withoutThemStatementFromProgress ||
    withoutThemStatementFromResult ||
    (lotPipesForDeadEnds != null
      ? `Found pipes that end the line in a ${lotPipesForDeadEnds.toLocaleString()}-pipe lot. Rerunning builds without them to measure the usable pool.`
      : 'Found pipes that end the line. Rerunning builds without them to measure the usable pool.');

  const step1LivePct =
    lotRuns > 0
      ? Math.min(95, Math.max(3, Math.round((lotRuns / Math.max(est, 30)) * 100)))
      : 3;

  // Sample UI: Step 2 stays at the "first 30" milestone (amber ~8%) — never mirrors Step 3's climbing bar.
  const step2LivePct = Math.min(
    12,
    Math.max(3, Math.round((Math.min(Math.max(lotRuns, 1), 30) / 30) * 8))
  );

  const shortlistStatement =
    (progress?.phase === 'shortlist' && progress.statement) ||
    (hasShortlistResult && result?.shortlist
      ? `Now checking a shortlist of ${result.shortlist.pipeIds.length} pipes with its own run.`
      : null) ||
    'Now checking a shortlist with its own run.';

  const shortlistPipesTotal =
    inShortlistPhase && progress?.pipesTotal != null
      ? progress.pipesTotal
      : result?.shortlist?.pipeIds.length ?? null;

  const s1 = statusOf(1);
  const s2 = statusOf(2);
  const s3 = statusOf(3);
  const s4 = statusOf(4);
  const s5 = statusOf(5);
  const sShort = statusOf(shortlistStep);

  return (
    <div className="space-y-3.5 max-w-6xl mx-auto w-full">
      {/* All lot steps always appear — sample-ui storyboard, status only changes */}
      <StepCard step={1} title="Just submitted" status={s1}>
        <p className="text-[15px] text-text-grey">
          Running the first 30. First read expected after about{' '}
          <span className="font-semibold text-blueprint-blue">~{est} runs</span>; the watch-list
          decides the real total.
        </p>
        <ProgressBar pct={pctFor(1, step1LivePct)} color={COLOR.red} status={s1} />
        <p className="mt-2 text-[12px] text-heritage-grey">
          {s1 === 'now'
            ? lotRuns > 0
              ? `${lotRuns.toLocaleString()} runs so far`
              : 'submitting — waiting for the first runs'
            : s1 === 'done'
              ? 'first read started'
              : 'waiting to start'}
        </p>
      </StepCard>

      <StepCard step={2} title="After the first 30" status={s2}>
        <p className="text-[15px] text-text-grey">
          <span className="font-semibold text-blueprint-blue">{weldLabel}</span> weld in — that
          number is already settled. Now pinning down{' '}
          <span className="font-semibold text-blueprint-blue">which pipes to watch</span>.
        </p>
        <ProgressBar
          pct={pctFor(2, step2LivePct)}
          color={COLOR.amber}
          status={s2}
        />
        <p className="mt-2 text-[12px] text-heritage-grey mb-2">
          {s2 === 'waiting'
            ? 'waiting for the first 30'
            : s2 === 'now' && lotRuns < 30
              ? `${lotRuns.toLocaleString()} runs so far`
              : '30 runs so far'}
        </p>
        {(s2 === 'now' || s2 === 'passed' || s2 === 'done') && (
          <div className="border-l-[3px] border-blueprint-blue bg-[rgba(0,39,63,0.05)] rounded-r-lg px-4 py-3 text-[13.5px] text-text-grey">
            <span className="font-semibold text-blueprint-blue">The count is set.</span> The extra
            runs are all about the watch-list, which needs more to hold still — often well past the
            first estimate.
          </div>
        )}
      </StepCard>

      <StepCard step={3} title="Running on" status={s3}>
        {lotWeldable != null && lotPipesTotal != null ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
              <StatCard
                value={lotWeldable}
                label="Weld in"
                sub={`of ${lotPipesTotal}${
                  s3 === 'done' || (watchListSettled && !lotRunsStillGoing) ? ', settled' : ''
                }`}
              />
              <StatCard
                value={lotGiveOrTake != null ? `±${lotGiveOrTake}` : '—'}
                label="Give or take"
                sub="watch-list narrowing"
              />
              <StatCard
                value={
                  s3 === 'done' || (watchListSettled && !lotRunsStillGoing)
                    ? 'settled'
                    : s3 === 'now' || s3 === 'passed'
                      ? 'going'
                      : '—'
                }
                label="Status"
                sub={
                  s3 === 'done' || (watchListSettled && !lotRunsStillGoing)
                    ? 'watch-list held'
                    : s3 === 'now' || s3 === 'passed'
                      ? 'not settled yet'
                      : 'waiting'
                }
              />
            </div>
            <ProgressBar pct={pctFor(3, lotSoftPct)} color={COLOR.red} status={s3} />
            <p className="mt-2 text-[12px] text-heritage-grey">
              {s3 === 'waiting'
                ? 'waiting for the watch-list run'
                : `${lotRuns.toLocaleString()} runs so far${
                    s3 === 'now' || s3 === 'passed'
                      ? ` — keeps going until the watch-list holds still or time runs out (toward ~${runTarget.toLocaleString()})`
                      : ''
                  }`}
            </p>
          </>
        ) : (
          <p className="text-[15px] text-text-grey">Waiting for progress numbers…</p>
        )}
      </StepCard>

      <StepCard step={4} title="Finalizing" status={s4}>
        <p className="text-[15px] text-text-grey">
          {expectWithoutThem
            ? 'Watch-list settled on the lot. Next the service checks for line-enders and may rerun without them.'
            : expectShortlist
              ? 'Watch-list settled on the lot. Next the shortlist gets its own verification run.'
              : 'Watch-list settled. The run is counting the handling and the turning comparison.'}
        </p>
        <ProgressBar pct={pctFor(4, 92)} color={COLOR.green} status={s4} />
        <p className="mt-2 text-[12px] text-heritage-grey">
          {s4 === 'done' ? (
            expectWithoutThem ? (
              'lot finished — dead-end check next'
            ) : expectShortlist ? (
              'lot finished — shortlist phase next'
            ) : (
              'complete — see Overview for the full report'
            )
          ) : s4 === 'now' ? (
            <>
              show <span className="font-semibold text-blueprint-blue">finalizing</span> here, never
              a frozen bar
            </>
          ) : (
            'waiting for the watch-list to settle'
          )}
        </p>
      </StepCard>

      {expectWithoutThem && (
        <StepCard step={5} title="Dead-end check and rerun" status={s5}>
          <p className="text-[15px] text-text-grey">
            The lot count is done
            {lotPipesForDeadEnds != null ? (
              <>
                {' '}
                (
                <span className="font-semibold text-blueprint-blue">
                  {lotPipesForDeadEnds.toLocaleString()} pipes
                </span>{' '}
                in the lot)
              </>
            ) : null}
            . A scan of the fit data found line-enders, and the whole count now reruns without them.
          </p>
          <p className="mt-2 text-[15px] text-text-grey">
            <span className="font-semibold text-blueprint-blue">{withoutThemStatement}</span>
          </p>
          <ProgressBar pct={pctFor(5, withoutThemSoftPct)} color={COLOR.red} status={s5} />
          <p className="mt-2 text-[12px] text-heritage-grey mb-2">
            {s5 === 'done'
              ? 'usable pool measured — see Overview'
              : s5 === 'now'
                ? withoutThemRuns > 0
                  ? `${withoutThemRuns.toLocaleString()} rerun builds so far — toward ~${runTarget.toLocaleString()}`
                  : `rerunning toward ~${runTarget.toLocaleString()} builds — takes about as long as the lot count`
                : 'waiting — only when the lot finds line-enders'}
          </p>
          <div className="border-l-[3px] border-readable-red bg-[rgba(196,0,31,0.05)] rounded-r-lg px-4 py-3 text-[13.5px] text-text-grey">
            <span className="font-semibold text-blueprint-blue">Healthy lots skip this step.</span>{' '}
            When it runs, Overview and the rankings use the usable-pool numbers after those
            line-enders are set aside.
          </div>
        </StepCard>
      )}

      {expectShortlist && (
        <StepCard step={shortlistStep} title="Shortlist phase" status={sShort}>
          <p className="text-[15px] text-text-grey">
            <span className="font-semibold text-blueprint-blue">{shortlistStatement}</span>
          </p>
          <ProgressBar
            pct={pctFor(shortlistStep, shortlistSoftPct > 0 ? shortlistSoftPct : 38)}
            color={COLOR.blue}
            status={sShort}
          />
          <p className="mt-2 text-[12px] text-heritage-grey mb-2">
            {sShort === 'done'
              ? 'shortlist verified — see the Shortlist tab'
              : sShort === 'now'
                ? shortlistRuns > 0
                  ? `${shortlistRuns.toLocaleString()} shortlist runs so far`
                  : 'checking shortlist — preparing fit data'
                : 'waiting for shortlist verification'}
          </p>
          <div className="border-l-[3px] border-blueprint-blue bg-[rgba(0,39,63,0.05)] rounded-r-lg px-4 py-3 text-[13.5px] text-text-grey">
            <span className="font-semibold text-blueprint-blue">
              These numbers are for the shortlist alone
              {shortlistPipesTotal != null ? ` (${shortlistPipesTotal} pipes)` : ''}
              , not the full lot.
            </span>{' '}
            The lot watch-list settling is not the finish — wait until this check completes
            before treating the report as done.
          </div>
        </StepCard>
      )}

      <div className="border-l-[3px] border-[#E6A100] bg-[rgba(230,161,0,0.10)] rounded-r-lg px-4 py-3 text-[13.5px] text-text-grey">
        {reportComplete ? (
          <>
            <span className="font-semibold text-blueprint-blue">Report complete.</span> Overview and
            the other tabs have the final answer. Run again from Set up to start a new report.
          </>
        ) : (
          <>
            <span className="font-semibold text-blueprint-blue">Then → the Overview tab</span> fills
            in with the final answer.
          </>
        )}
      </div>
    </div>
  );
};
